import React, { useEffect, useState } from "react";
import Radium from "radium";
import lightenDarkenColor from "../../utils/lightenDarkenColor";
import { DIAL_TYPES, dials } from "./constants";
import { getStyles } from "./getStyles";
import "./doubleDials.scss";

const DoubleDials = (props) => {
  const colors = {
    dial1BgColor: lightenDarkenColor(props.dial1Color, -0.84),
    dial2BgColor: lightenDarkenColor(props.dial2Color, -0.84),
    dial1ShowColor: lightenDarkenColor(props.dial1Color, -0.3),
    dial2ShowColor: lightenDarkenColor(props.dial2Color, -0.3)
  };

  const [dialAnimations, setDialAnimations] = useState({
    dial1FirstAnimation: "",
    dial1SecondAnimation: "",
    dial1AnimationEnd: "",
    dial2FirstAnimation: "",
    dial2SecondAnimation: "",
    dial2AnimationEnd: "",
    showCurrent: false,
    showPacing: false
  });

  const renderDial = (props, type, number, animType = "FirstAnimation") => {
    const value =
      type === DIAL_TYPES.CURRENT ? props.currentData[0].value : props.pacingData[0].value;
    const percent = Math.max(value, 0) / 100;
    const angle = Math.round(percent * 360);
    const styleSheet = document.styleSheets[0];
    let keyframes;

    if (angle - 180 > 180 && animType !== "AnimationEnd") {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
      0%,50% {transform: rotateZ(180deg);}
      100% {transform: rotateZ(180deg);}
    }`;
    } else if (angle > 180) {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
        0% {transform: none;}
        50% {transform: rotateZ(180deg);}
        100% {transform: rotateZ(${animType === "FirstAnimation" ? "180" : angle}deg);}
      }`;

      if (animType === "SecondAnimation") {
        keyframes = `@keyframes dial${number}SecondAnimation-${angle} {
                         0%,50% {transform: rotateZ(0deg);}
                         100% {transform: rotateZ(${angle - 180}deg);}
                     }`;
      }
    } else {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
        0% {transform: none;}
        100% {transform: rotateZ(${angle}deg);}
      }`;

      if (animType === "SecondAnimation") {
        keyframes = `@keyframes dial${number}SecondAnimation-${angle} {
        0%,50% {transform: rotateZ(0deg);}
        100% {transform: rotateZ(0deg);}
      }`;
      }
    }

    styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

    setDialAnimations((prevState) => ({
      ...prevState,
      [`dial${number}${animType}`]: `dial${number}${animType}-${angle}`
    }));
  };

  useEffect(() => {
    if (dials.length) {
      dials.forEach((dial) => {
        renderDial(props, dial.type, dial.number);
        renderDial(props, dial.type, dial.number, "SecondAnimation");
        renderDial(props, dial.type, dial.number, "AnimationEnd");
      });
    }
  }, [dials]);

  const handleMouseChange = (type, shouldShow) => () => {
    const keyToUpdate = type === DIAL_TYPES.CURRENT ? "showCurrent" : "showPacing";
    setDialAnimations((prevState) => ({
      ...prevState,
      [keyToUpdate]: shouldShow
    }));
  };

  const { dial1Color, dial2Color } = props;
  const { dial1BgColor, dial2BgColor, dial1ShowColor, dial2ShowColor } = colors;
  const styles = getStyles(dialAnimations);

  return (
    <div style={{ fontSize: "16px" }}>
      <div className="screen">
        <div
          className="percent-display"
          style={{
            color: dial1Color,
            opacity: dialAnimations.showCurrent ? 1 : 0
          }}
        >
          {Math.round(props.currentData[0].value)}%
        </div>
        <div
          className="percent-display"
          style={{
            color: dial2Color,
            opacity: dialAnimations.showPacing ? 1 : 0
          }}
        >
          {Math.round(props.pacingData[0].value)}%
        </div>
        <div className="dials">
          <div className="dial move">
            <div
              className="dial-background"
              style={{
                backgroundColor: dial1BgColor
              }}
            />
            <div className="dial-container container1">
              <div
                className="wedge"
                style={{
                  ...styles.dial1FirstAnimation,
                  background: dialAnimations.showCurrent ? dial1ShowColor : dial1Color
                }}
                onMouseEnter={handleMouseChange(DIAL_TYPES.CURRENT, true)}
                onMouseLeave={handleMouseChange(DIAL_TYPES.CURRENT, false)}
              />
            </div>
            <div className="dial-container container2">
              <div
                className="wedge"
                style={{
                  ...styles.dial1SecondAnimation,
                  background: dialAnimations.showCurrent ? dial1ShowColor : dial1Color
                }}
                onMouseEnter={handleMouseChange(DIAL_TYPES.CURRENT, true)}
                onMouseLeave={handleMouseChange(DIAL_TYPES.CURRENT, false)}
              />
            </div>
            <div
              className="marker end"
              style={{
                ...styles.dial1AnimationEnd,
                background: dialAnimations.showCurrent ? dial1ShowColor : dial1Color
              }}
              onMouseEnter={handleMouseChange(DIAL_TYPES.CURRENT, true)}
              onMouseLeave={handleMouseChange(DIAL_TYPES.CURRENT, false)}
            />
          </div>
          {props.hideForecast ? null : (
            <div className="dial exercise" data-testid="forecast-dial">
              <div
                className="dial-background"
                style={{
                  backgroundColor: dial2BgColor
                }}
              />
              <div className="dial-container container1">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial2FirstAnimation,
                    background: dialAnimations.showPacing ? dial2ShowColor : dial2Color
                  }}
                  onMouseEnter={handleMouseChange(DIAL_TYPES.PACING, true)}
                  onMouseLeave={handleMouseChange(DIAL_TYPES.PACING, false)}
                />
              </div>
              <div className="dial-container container2">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial2SecondAnimation,
                    background: dialAnimations.showPacing ? dial2ShowColor : dial2Color
                  }}
                  onMouseEnter={handleMouseChange(DIAL_TYPES.PACING, true)}
                  onMouseLeave={handleMouseChange(DIAL_TYPES.PACING, false)}
                />
              </div>
              <div
                className="marker end"
                style={{
                  ...styles.dial2AnimationEnd,
                  background: dialAnimations.showPacing ? dial2ShowColor : dial2Color
                }}
                onMouseEnter={handleMouseChange(DIAL_TYPES.PACING, true)}
                onMouseLeave={handleMouseChange(DIAL_TYPES.PACING, false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DoubleDials.defaultProps = {
  dial1Color: "#4a9bff",
  dial2Color: "#fc6a3e"
};

export default Radium(DoubleDials);
